import React from "react";
import { Dropdown } from "react-bootstrap";
import { FieldArrayWithId, UseFieldArrayRemove, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { FormInput } from "go-form";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { ReactComponent as EditSVG } from "go-core/images/svg/actions/edit.svg";
import { ReactComponent as RemoveSVG } from "go-core/images/svg/actions/remove.svg";
import { ReactComponent as DropdownSVG } from "go-core/images/svg/dropdown.svg";
import FormNumberInput from "go-form/components/FormNumberInput";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { ReactComponent as MoreSVG } from "../../../../../../../../../../../../images/svg/documents/more.svg";
import {
	BundleApi,
	BundleItemApi,
	ProductApi,
	ProductItemApi,
	ProductStateApi,
} from "../../../../../../../../../../../../services/Api/Organization/types";
import { isItemRowIndexShown, showMore } from "../../../../../../utils/itemsTableUtils";
import {
	drawProductState,
	getInStockAmountBadgeColor,
	getStateForProduct,
} from "../../../../../../utils/productStateUtils";
import BundleFormItemsSummaryRow from "./BundleFormItemsSummaryRow";
import BundleFormItemsTableBodySubRow from "./BundleFormItemsTableBodySubRow";

interface Props {
	form: UseFormReturn<BundleApi>;
	watchedItems: BundleItemApi[];
	fields: FieldArrayWithId<BundleApi, "items", "key">[];
	searchProducts: (
		search: string,
		params: Record<string, any>,
		options?: Record<string, any> | undefined
	) => Promise<ProductApi[]>;
	shownIndexes: number[];
	setShownIndexes: (newIndexes: number[]) => void;
	setShowChangeProductModal: (value: number | null) => void;
	destinationProductStates: ProductStateApi[];
	sourceProductStates: ProductStateApi[];
	handleChangeProduct: (product: ProductApi, index: number) => void;
	remove: UseFieldArrayRemove;
	fetchProductItems: (productId: number) => Promise<ProductItemApi[]>;
	setShouldFetchSourceProductStates: (value: boolean) => void;
}

const BundleFormItemsTableBody = ({
	form,
	watchedItems,
	fields,
	searchProducts,
	shownIndexes,
	setShownIndexes,
	setShowChangeProductModal,
	destinationProductStates,
	sourceProductStates,
	handleChangeProduct,
	fetchProductItems,
	remove,
	setShouldFetchSourceProductStates,
}: Props) => {
	const {
		formState: { errors },
		register,
		control,
		watch,
		setValue,
	} = form;
	const organization = useSelector(selectOrganization);
	const { t } = useTranslation();

	const getProductStates = (type: "destination" | "source", product?: ProductApi) => {
		if (!product) return;

		return type === "destination" ? [...destinationProductStates] : [...sourceProductStates];
	};

	const drawInStockBadge = (type: "destination" | "source", product: ProductApi) => {
		const productStates = getProductStates(type, product);
		if (!productStates) return;

		return (
			<span className={`ms-1 badge bg-${getInStockAmountBadgeColor(product, productStates)}`}>
				{`${getStateForProduct(product, productStates)?.in_stock_amount} ${t(
					`enums.products.measure_types.${product?.measure_type}`
				)}`}
			</span>
		);
	};

	const handleRemoveItem = (index: number) => {
		remove(index);
	};

	const handleRestoreDefaultComponents = async (productId: number, itemIndex: number, item: BundleItemApi) => {
		const productItems = await fetchProductItems(productId);
		const itemQuantity = item.quantity;
		setValue(
			`items.${itemIndex}.items`,
			productItems.map((productItem) => ({
				product_id: productItem.product_id,
				product_name: productItem.product_name,
				state: productItem.state,
				quantity: (itemQuantity || 0) * productItem.amount,
				unit_fifo_cost_net_money: productItem.unit_product_cost_money,
				total_fifo_cost_net_money: null,
				measure_type: productItem.measure_type,
				amount: productItem.amount,
			}))
		);
	};
	const handleChangeQuantity = (item: BundleItemApi, itemIndex: number, value?: number) => {
		if (value === null || value === undefined) return;

		setValue(`items.${itemIndex}.quantity_produced`, value);

		let newItemItems = [...(item.items || [])];

		if (newItemItems.length === 0) return;
		newItemItems = newItemItems.map((newItem) => {
			if (newItem.amount === null || newItem.amount === undefined) return newItem;
			let quantity = 0;

			if (value && newItem.amount) {
				quantity = newItem.amount * value;
			} else {
				quantity = 0;
			}

			return {
				...newItem,
				quantity,
			};
		});

		setValue(`items.${itemIndex}.items`, newItemItems);
	};

	return (
		<tbody>
			{watchedItems.map((item, index) => (
				<React.Fragment key={fields[index].key}>
					<tr>
						<td className="action">
							<FormInput
								errors={errors}
								type="hidden"
								register={register}
								name={`items.${index}.position`}
								defaultValue={index}
							/>
							<FormInput
								type="hidden"
								errors={errors}
								register={register}
								name={`items.${index}.id`}
								defaultValue={item.id}
							/>
						</td>
						<td>
							{!item?.product?.id ? (
								<div className="d-flex align-items-center">
									<span className="me-2 d-inline-block">{`${index + 1}.`}</span>
									<FormSelectGroup
										name={`items.${index}.product.name`}
										errors={errors}
										onChange={(_, product) => handleChangeProduct(product, index)}
										defaultValue={{
											label: item?.product?.name,
											id: item?.product?.id,
										}}
										loadOptions={searchProducts}
										control={control}
										data-testid={`items.${index}.product.name`}
									/>
									<FormInput
										type="hidden"
										errors={errors}
										register={register}
										name={`items.${index}.product_id`}
										defaultValue={item.product_id || ""}
									/>
								</div>
							) : (
								<div className="d-flex align-items-center product-box">
									<span className="me-2">{`${index + 1}.`}</span>
									<DropdownSVG
										className="icon"
										id={`icon-${index}`}
										style={{
											transform: isItemRowIndexShown(shownIndexes, index)
												? "rotate(180deg)"
												: "unset",
										}}
										onClick={() => showMore(shownIndexes, setShownIndexes, index)}
									/>
									<Link
										rel="noreferrer"
										target="_blank"
										to={`/${organization.id}/products/${item.product.id}/edit`}
										className="mx-2"
									>
										{item.product.name}
									</Link>
									<EditSVG
										className="me-2 icon edit-icon"
										onClick={() => setShowChangeProductModal(index)}
									/>
									{drawInStockBadge("destination", item?.product)}
								</div>
							)}
						</td>
						<td>
							<FormNumberInput
								errors={errors}
								name={`items.${index}.quantity`}
								suffix=""
								defaultValue={Number(item.quantity)}
								control={control}
								onBlur={(value) => handleChangeQuantity(item, index, value)}
								allowEmpty
							/>
						</td>
						<td>
							<FormNumberInput
								errors={errors}
								name={`items.${index}.quantity_produced`}
								suffix=""
								defaultValue={Number(item.quantity_produced)}
								control={control}
							/>
						</td>
						{fields.length > 1 && (
							<td
								className={`${
									!((fields.length === 1 && index === 0) || index === fields.length - 1)
										? "actions"
										: "action"
								}`}
							>
								<div className="d-flex align-items-center gap-2">
									{!((fields.length === 1 && index === 0) || index === fields.length - 1) && (
										<RemoveSVG className="icon" onClick={() => handleRemoveItem(index)} />
									)}
									{item?.product_id && (
										<Dropdown className="icon">
											<Dropdown.Toggle as={MoreSVG} />
											<Dropdown.Menu>
												<Dropdown.Item
													onClick={() =>
														handleRestoreDefaultComponents(
															item.product_id as number,
															index,
															item
														)
													}
												>
													{t("modules.bundle.action.restore_default_components.title")}
												</Dropdown.Item>
											</Dropdown.Menu>
										</Dropdown>
									)}
								</div>
							</td>
						)}
					</tr>
					{isItemRowIndexShown(shownIndexes, index) &&
						item.product &&
						getStateForProduct(item?.product, getProductStates("destination", item?.product) || []) &&
						getStateForProduct(item?.product, getProductStates("source", item?.product) || []) && (
							<tr>
								<td className="action" />
								<td>
									<div className="d-flex flex-column">
										<small className="text-muted font-weight-bold mb-1">
											{`${t("modules.bundle.field.destination_stock.title")}: ${
												watch("destination_stock")?.name ?? "-"
											}`}
										</small>
										{item?.product?.type !== "COMPOSITE" && (
											<small className="text-muted font-weight-bold">
												{`${t("common.word.last_price")}: ${FormatMoney(
													item?.product?.last_price_money
												)}`}
											</small>
										)}
										<small className="text-muted font-weight-bold">
											{`${t("common.word.cost")}: ${FormatMoney(
												item?.product?.cost_price_money
											)}`}
										</small>
										<small className="text-muted font-weight-bold">
											{drawProductState(
												item.product,
												getProductStates("destination", item?.product) || [],
												t
											)}
										</small>
										<small className="text-muted font-weight-bold">
											{`${t("common.word.default_volume")}: ${
												item?.product?.default_volume ? item?.product?.default_volume : "-"
											}`}
										</small>
									</div>
								</td>
								<td colSpan={3}>
									<FormInput
										errors={errors}
										register={register}
										className="comment-input"
										name={`items.${index}.comment`}
										defaultValue={item?.comment}
										placeholder={t("common.word.comment.placeholder")}
									/>
								</td>
							</tr>
						)}
					{item?.product?.id && (
						<BundleFormItemsTableBodySubRow
							form={form}
							itemIndex={index}
							drawInStockBadge={drawInStockBadge}
							setShouldFetchSourceProductStates={setShouldFetchSourceProductStates}
						/>
					)}
				</React.Fragment>
			))}
			<BundleFormItemsSummaryRow items={watchedItems.map((item) => item.items || []).flat()} />
		</tbody>
	);
};

export default BundleFormItemsTableBody;
